"use client";

import type { NubeSDKRuntimeState } from "@tiendanube/nube-sdk-internal-types";
import { useEffect, useState } from "@tiendanube/nube-sdk-react-runtime";
import type { UISlot } from "@tiendanube/nube-sdk-types";
import { DynamicRenderWithUI, type NubeComponent } from "../dynamic-render";

// Type guards to detect component types
function isNubeComponent(value: unknown): value is NubeComponent {
	return (
		typeof value === "object" &&
		value !== null &&
		"type" in value &&
		typeof (value as NubeComponent).type === "string"
	);
}

function isNubeComponentArray(value: unknown): value is NubeComponent[] {
	return Array.isArray(value) && value.every((item) => isNubeComponent(item));
}

function isNubeComponentRecord(
	value: unknown,
): value is Record<string, NubeComponent> {
	return (
		typeof value === "object" &&
		value !== null &&
		!Array.isArray(value) &&
		!isNubeComponent(value) &&
		Object.values(value).every((item) => isNubeComponent(item))
	);
}

export type SlotProps = {
	id: UISlot;
	className?: string;
	disabledComponents?: string[];
	pick?: number | string;
	style?: React.CSSProperties;
};

// Function to resolve component based on pick parameter
function resolveComponent(
	slotContent: unknown,
	pick?: number | string,
): NubeComponent | null {
	if (!slotContent) return null;

	// If no pick provided, handle backward compatibility
	if (pick === undefined) {
		// If it's already a NubeComponent, return as is (backward compatibility)
		if (isNubeComponent(slotContent)) {
			return slotContent;
		}
		// If it's an array, return first element for backward compatibility
		if (isNubeComponentArray(slotContent)) {
			return slotContent[0] || null;
		}
		// If it's a record, return null (can't determine which to pick)
		return null;
	}

	// Handle picking from array
	if (isNubeComponentArray(slotContent)) {
		const raw = typeof pick === "number" ? pick : Number(pick);
		const index = Number.isInteger(raw) ? raw : Number.NaN;
		const invalidIndex =
			!Number.isInteger(index) || index < 0 || index >= slotContent.length;

		if (invalidIndex) return null;

		return slotContent[index] || null;
	}

	if (isNubeComponentRecord(slotContent)) {
		const key = String(pick);
		return slotContent[key] || null;
	}

	// If it's a single component and pick is provided, ignore pick for backward compatibility
	if (isNubeComponent(slotContent)) {
		return slotContent;
	}

	return null;
}

// Stop the synthetic event from propagating to ancestor React handlers.
// React's stopPropagation also calls native stopPropagation under the hood,
// which stops the native bubble at the React root (so native handlers above
// the root won't fire either).
function stopPropagation(event: React.SyntheticEvent) {
	event.stopPropagation();
}

// Same as above, plus preventDefault — only used on click. This suppresses
// the parent's native activation (e.g. <a> navigation, submit button form
// submission) without blocking default behaviors on mouse/touch/key events
// such as text selection, typing or touch-driven scroll.
function stopAndPreventDefault(event: React.SyntheticEvent) {
	event.stopPropagation();
	if (event.cancelable) {
		event.preventDefault();
	}
}

export function Slot({
	id,
	pick,
	disabledComponents,
	className,
	style,
}: SlotProps) {
	const [state, setState] = useState<NubeSDKRuntimeState | null>(null);

	useEffect(() => {
		if (typeof window === "undefined" || !window?.nubeSDK) {
			return;
		}

		// Initialize state with current SDK state
		setState(window.nubeSDK.getState());

		// Create a stable function reference for both on and off
		const updateState = (newState: NubeSDKRuntimeState) => {
			setState(newState);
		};

		window.nubeSDK.on("ui:slot:set", updateState);

		return () => {
			window.nubeSDK?.off("ui:slot:set", updateState);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Safe type assertion since we handle undefined/null cases in resolveComponent
	const slotContent = state?.ui?.slots?.[id as UISlot];

	const component = resolveComponent(slotContent, pick);

	if (!component) return null;

	// React event handlers (not native addEventListener) are required here:
	// React 17+ delegates events to the root container, so attaching a native
	// listener on the wrapper that calls stopPropagation would stop the native
	// bubble before it reaches the root — and React would never dispatch its
	// synthetic events, breaking onClick handlers on slot descendants.
	return (
		<div
			className={className}
			data-slot={id}
			id={`nube-sdk-slot-${id}`}
			style={style}
			onClick={stopAndPreventDefault}
			onMouseDown={stopPropagation}
			onMouseUp={stopPropagation}
			onTouchStart={stopPropagation}
			onTouchEnd={stopPropagation}
			onPointerDown={stopPropagation}
			onPointerUp={stopPropagation}
			onKeyDown={stopPropagation}
			onKeyUp={stopPropagation}
			onKeyPress={stopPropagation}
		>
			<DynamicRenderWithUI
				component={component}
				disabledComponents={disabledComponents}
			/>
		</div>
	);
}
